import React, { useState } from 'react';
import { vansAPI, warehouseAPI } from '../lib/api';
import { checkPlanLimit, handleApiError, showPlanLimitToast } from '../lib/apiErrors';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Shared vehicle creation dialog — same form, validation, and backend
 * call used on /vans, reusable anywhere (e.g. /team Add Member drawer).
 *
 * @param {boolean}  open        - Dialog open state
 * @param {function} onOpenChange - Dialog open/close handler
 * @param {string}   prefillName  - Optional pre-filled vehicle name
 * @param {function} onCreated    - Called with the new vehicle object on success
 */
export function CreateVehicleDialog({ open, onOpenChange, prefillName = '', onCreated }) {
  const { isOwner, permissions } = useAuth();
  const canManageBilling = isOwner || permissions?.['billing.manage'] === true;

  const [formData, setFormData] = useState({ name: prefillName, van_number: '', license_plate: '' });
  const [saving, setSaving] = useState(false);

  // Reset form when dialog opens with new prefill
  React.useEffect(() => {
    if (open) {
      setFormData({ name: prefillName, van_number: '', license_plate: '' });
    }
  }, [open, prefillName]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return; // prevent duplicate

    // Plan-limit check
    try {
      const limitsRes = await warehouseAPI.getPlanLimits();
      const limits = limitsRes.data;
      const { canCreate, message } = checkPlanLimit(limits, 'vehicle');
      if (!canCreate) {
        showPlanLimitToast(message, 'vehicle', { canUpgrade: canManageBilling });
        return;
      }
    } catch {
      // If limits call fails, proceed — backend will enforce
    }

    setSaving(true);
    try {
      const res = await vansAPI.create(formData);
      toast.success('Vehicle created');
      onOpenChange(false);
      onCreated?.(res.data);
    } catch (error) {
      handleApiError(error, { defaultMessage: 'Failed to create vehicle' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="create-vehicle-dialog">
        <DialogHeader>
          <DialogTitle>Add New Vehicle</DialogTitle>
          <DialogDescription>Add a new service vehicle to your fleet</DialogDescription>
        </DialogHeader>
        <form id="create-vehicle-form" onSubmit={handleSubmit} className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="cv-name" className="text-sm font-bold uppercase tracking-wide">
              Vehicle Name *
            </Label>
            <Input
              id="cv-name"
              placeholder="e.g., Vehicle #1, Mike's Van"
              value={formData.name}
              onChange={(e) => setFormData(f => ({ ...f, name: e.target.value }))}
              required
              autoFocus
              className="h-12 text-lg"
              data-testid="cv-name-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cv-number" className="text-sm font-bold uppercase tracking-wide">
              Vehicle Number
            </Label>
            <Input
              id="cv-number"
              placeholder="e.g., V001"
              value={formData.van_number}
              onChange={(e) => setFormData(f => ({ ...f, van_number: e.target.value }))}
              className="h-12 text-lg"
              data-testid="cv-number-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cv-plate" className="text-sm font-bold uppercase tracking-wide">
              License Plate
            </Label>
            <Input
              id="cv-plate"
              placeholder="e.g., ABC-1234"
              value={formData.license_plate}
              onChange={(e) => setFormData(f => ({ ...f, license_plate: e.target.value }))}
              className="h-12 text-lg"
              data-testid="cv-plate-input"
            />
          </div>
        </form>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="submit" form="create-vehicle-form" disabled={saving} data-testid="cv-save-btn">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : 'Add Vehicle'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
